import React, { useEffect, useState } from "react"
import qs from "query-string"
import styled from "styled-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faUserPlus,
  faPlus,
  faUserCircle,
  faRefresh,
  faLink,
  faTrash,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons"
import theme from "../../stylesheets/theme"
import { useMutate } from "../../hooks/useMutate"
import { closeInboxModal, sendToastMessageToInbox } from "../../components/util"

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const MembersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 4px;
  border-bottom: 1px solid ${theme.gray1};

  background-color: ${theme.white};
  
  .left-section {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${theme.gray0};
    }
    
    .group-icon {
      color: ${theme.gray6};
      font-size: 16px;
    }
    
    .title {
      font-weight: 600;
      font-size: 12px;
      color: ${theme.gray8};
    }
    
    .member-count {
      background: ${theme.gray4};
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      color: ${theme.gray10};
      margin-left: 8px;
    }
  }
  
  .right-section {
    display: flex;
    align-items: center;
    gap: 8px;

    .add-member {
      display: flex;
      align-items: center;
      gap: 6px;
      color: ${(props) => (props.$disabled ? theme.gray3 : theme.primary)};
      font-size: 12px;
      font-weight: 600;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: ${(props) => (props.$disabled ? "auto" : "pointer")};
      transition: background-color 0.2s ease;
      
      &:hover {
        background-color: ${(props) => (props.$disabled ? "transparent" : theme.gray1)};
      }
      
      svg {
        font-size: 10px;
      }
      
      span {
        color: ${(props) => (props.$disabled ? theme.gray3 : theme.primary)};
      }
    }
  }
  
  .chevron-icon {
    color: ${theme.gray5};
    transition: transform 0.2s ease;
    transform: ${(props) => (props.isExpanded ? "rotate(180deg)" : "rotate(0deg)")};
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    
    &:hover {
      background-color: ${theme.gray1};
    }
  }
`

const MembersContent = styled.div`
  display: ${(props) => (props.isExpanded ? "flex" : "none")};
  flex-direction: column;
  flex: 1;
  padding: 4px;
  background-color: ${theme.gray0};
  overflow: hidden;
`

const InviteSection = styled.div`
  background: ${theme.white};
  border-radius: 8px;
  margin-bottom: 2px;
`

// const InviteTitle = styled.h3`
//   margin: 0 0 8px 0;
//   font-size: 14px;
//   font-weight: 500;
//   color: ${theme.gray12};
//   display: flex;
//   align-items: center;
//   gap: 8px;
// `

const InviteLink = styled.div`
  background: ${theme.white};
  padding: 10px 4px 15px 4px;
  border-radius: 8px;
  line-height: 1.5;
  overflow-wrap: break-word;
  word-break: break-all;
  position: relative;
  overflow: visible; /* Ensure tooltips can be displayed outside container */
`

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.gray12};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 9999;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  margin-top: 5px;
  min-width: max-content;
  max-width: 200px;
  
  /* Ensure tooltip stays within viewport */
  @media (max-width: 400px) {
    left: 0;
    transform: none;
    right: auto;
  }
  
  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: ${theme.gray12};
    
    @media (max-width: 400px) {
      left: 20px;
      transform: none;
    }
  }
`

const InviteLinkText = styled.span`
  font-size: 14px;
  color: ${theme.primary};
  text-decoration: none;
  cursor: pointer;
  margin-right: 8px;
  display: inline-block;
  word-break: break-all;
  overflow-wrap: break-word;
  position: relative;
  max-width: calc(100% - 120px); /* Leave space for buttons */
  
  &:hover {
    text-decoration: underline;
    color: ${theme.primary};
  }
`

const LinkTooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 20%;
  background: ${theme.gray12};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 9999;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  margin-top: 5px;
  min-width: max-content;
  max-width: 200px;
  
  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 20px;
    border: 5px solid transparent;
    border-bottom-color: ${theme.gray12};
  }
`

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${theme.gray6};
  cursor: pointer;
  padding: 4px;
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  &.copy {
    color: ${theme.primary};
  }

  &.refresh {
    color: #22c55e;
  }
  
  &.delete {
    color: #ef4444;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 14px;
  }
  
  /* Tooltip positioning for icon buttons */
  ${Tooltip} {
    left: 50%;
    transform: translateX(-50%);
    
    &::before {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`

const MembersSection = styled.div`
  background: ${theme.white};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  flex: ${(props) => (props.hasMembers ? "1" : "0 0 auto")};
  display: flex;
  flex-direction: column;
  min-height: ${(props) => (props.hasMembers ? "auto" : "0")};
`

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${(props) => (props.hasMembers ? "1" : "0 0 auto")};
  overflow-y: ${(props) => (props.hasMembers ? "auto" : "visible")};
  max-height: ${(props) => (props.hasMembers ? "100%" : "auto")};
`

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${theme.gray1};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${theme.gray0};
  }
`

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const MemberDetails = styled.div`
  display: flex;
  flex-direction: column;
`

const MemberName = styled.div`
  font-weight: 500;
  color: ${theme.gray12};
  font-size: 14px;
`

const MemberSubtitle = styled.div`
  font-size: 12px;
  color: #CDCDCD;
`

const MemberStatus = styled.div`
  font-size: 12px;
  color: ${theme.gray8};
  background: ${theme.gray3};
  padding: 4px 8px;
  border-radius: 12px;
`

// const AdminBadge = styled.div`
//   font-size: 12px;
//   color: #399918;
//   background: #9CDBA6;
//   padding: 4px 8px;
//   border-radius: 12px;
//   font-weight: 500;
// `

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${theme.gray8};
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  
  &:hover {
    background: ${theme.gray3};
    color: ${theme.gray10};
  }
  
  &.danger:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`

const RequestActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  min-width: 32px;
  min-height: 32px;
  
  &.approve {
    color: #22c55e;
    
    &:hover {
      background: rgba(34, 197, 94, 0.1);
      color: #16a34a;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  &.reject {
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.1);
      color: #dc2626;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  svg {
    font-size: 14px;
  }
`

const ActionTooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.gray12};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 9999;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  margin-top: 5px;
  pointer-events: none;
  
  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: ${theme.gray12};
  }
`

// Skeleton loading component
const SkeletonMemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${theme.gray1};
  
  &:last-child {
    border-bottom: none;
  }
`

const SkeletonMemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SkeletonAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
`

const SkeletonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SkeletonLine = styled.div`
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  &.name {
    width: 120px;
  }
  
  &.subtitle {
    width: 80px;
    height: 12px;
  }
`

const SkeletonStatus = styled.div`
  width: 60px;
  height: 24px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
`

const SkeletonContainer = styled.div`
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const TabContainer = styled.div`
  display: flex;
  background: ${theme.white};
  margin-bottom: 4px;
  border-radius: 8px;
  overflow: hidden;
`

const Tab = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: ${(props) => (props.active ? "#F0F2F5" : "transparent")};
  color: ${(props) => {
    if (props.isMembersTab) return "#22c55e"
    if (props.isJoinRequestsTab) return theme.gray8
    return theme.gray8
  }};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: ${(props) => (props.active ? "#F0F2F5" : theme.gray1)};
  }
  
  .count {
    background: ${(props) => {
    if (props.isMembersTab) return "#22c55e"
    if (props.isJoinRequestsTab) return "#ef4444"
    return "#22c55e"
  }};
    padding: 2px 8px;
    border-radius: 8px;
    font-size: 11px;
    color: ${theme.white};
    min-width: 18px;
    text-align: center;
  }
`
const DeleteGroupButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ef4444;
  font-size: 14px;
  cursor: pointer;
  padding: 12px 16px;
  margin-top: 16px;
  border-top: 1px solid ${theme.gray1};
  
  &:hover {
    opacity: 0.8;
  }
  
  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .delete-icon {
    font-size: 14px;
  }
`

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

// Skeleton Member Component
const SkeletonMember = () => (
  <SkeletonContainer>
    <SkeletonMemberRow>
      <SkeletonMemberInfo>
        <SkeletonAvatar />
        <SkeletonDetails>
          <SkeletonLine className="name" />
          <SkeletonLine className="subtitle" />
        </SkeletonDetails>
      </SkeletonMemberInfo>
      <SkeletonStatus />
    </SkeletonMemberRow>
  </SkeletonContainer>
)

// Multiple Skeleton Rows Component
const MemberListSkeleton = ({ count = 3 }) => (
  <>
    {Array.from({ length: count }, (_, index) => (
      <SkeletonMember key={index} />
    ))}
  </>
)

const SERVER_API_PATH_GROUP = process.env.REACT_APP_SERVER_GROUP_API_PATH || process.env.REACT_APP_SERVER_API_PATH

const GroupMembers = ({ location }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [groupData, setGroupData] = useState({})
  const [members, setMembers] = useState([])
  const [joinRequests, setJoinRequests] = useState([])
  const [activeTab, setActiveTab] = useState("members") // "members" or "requests"
  const [loading, setLoading] = useState(false)
  const [loadingJoinRequests, setLoadingJoinRequests] = useState(false)
  const [error, setError] = useState(null)
  const [joinRequestsError, setJoinRequestsError] = useState(null)
  const [dropdownVisible, setDropdownVisible] = useState(null)
  const [joinRequestDropdownVisible, setJoinRequestDropdownVisible] = useState(null)
  const [popupVisible, setPopupVisible] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [copyTooltipVisible, setCopyTooltipVisible] = useState(false)
  const [refreshingLink, setRefreshingLink] = useState(false)
  // const [deletingLink, setDeletingLink] = useState(false)
  const [deletingGroup, setDeletingGroup] = useState(false)
  const [groupProfilePicUrl, setGroupProfilePicUrl] = useState("")
  const [approvingRequests, setApprovingRequests] = useState({}) // Track which requests are being approved
  const [actionTooltips, setActionTooltips] = useState({}) // Track tooltip visibility for action buttons

  // Parse URL parameters from iframe
  const query = qs.parse(location?.search || window.location.search)
  const {
    groupId,
    wChannelId,
    sectionId = 1, // Get section ID from URL parameters
    contextToken,
  } = query

  // Extract actual group ID by removing "GROUP-" prefix
  const extractedGroupId = groupId?.startsWith("GROUP-") ? groupId.substring(6) : groupId

  // Update Group
  const updateGroup = useMutate({
    url: `${SERVER_API_PATH_GROUP}/api/group`,
    contextToken,
    method: "PATCH",
    errorPaths: selectedMember
      ? ["error", "errorMessage", "error.message", "addParticipants.error.message", "removeParticipants.error.message"]
      : ["error", "errorMessage", "error.message"],
    onSuccess: () => {
      sendToastMessageToInbox({
        message: "Group updated successfully",
        type: "success",
        options: {
          duration: 15000,
        },
      })
      // If a member was removed, update the local state
      if (selectedMember) {
        setMembers((prevMembers) => prevMembers.filter((member) => member.id !== selectedMember.id))

        // Also update the group data member count
        setGroupData((prevGroupData) => {
          if (prevGroupData) {
            return {
              ...prevGroupData,
              totalParticipants: Math.max((prevGroupData.totalParticipants || 0) - 1, 0),
            }
          }
          return prevGroupData
        })

        // Clear the selected member
        setSelectedMember(null)
      }

      closeInboxModal()
    },
    onFailure: () => {
      // Clear the selected member even on failure
      if (selectedMember) {
        setSelectedMember(null)
      }
    },
  })

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "PROFILE_PIC_URL") {
        const { profilePicUrl } = event.data.payload
        // Use the profilePicUrl here
        setGroupProfilePicUrl(profilePicUrl)
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [groupProfilePicUrl])

  // Update group profile picture when groupProfilePicUrl changes
  useEffect(() => {
    if (groupProfilePicUrl && extractedGroupId && groupData && (!groupData.profilePictureUrl || groupData.profilePictureUrl !== groupProfilePicUrl)) {
      updateGroup.mutate({
        groupId: extractedGroupId,
        // eslint-disable-next-line camelcase
        profile_picture_file: {
          url: groupProfilePicUrl,
        },
      })
    } else {
      // Conditions not met for API call
    }
  }, [groupProfilePicUrl, extractedGroupId, groupData])

  // Delete Group
  const deleteGroup = useMutate({
    url: `${SERVER_API_PATH_GROUP}/api/group`,
    contextToken,
    method: "DELETE",
    errorPaths: ["error", "errorMessage", "error.message"],
    onSuccess: () => {
      setDeletingGroup(false)
      sendToastMessageToInbox({
        message: "Group deleted successfully",
        type: "success",
        options: {
          duration: 15000,
        },
      })
      closeInboxModal()
    },
    onError: () => {
      setDeletingGroup(false)
      sendToastMessageToInbox({
        message: "Something went wrong. Failed to delete group",
        type: "error",
        options: {
          duration: 15000,
        },
      })
    },
    onFailure: () => {
      setDeletingGroup(false)
      sendToastMessageToInbox({
        message: "Something went wrong. Failed to delete group",
        type: "error",
        options: {
          duration: 15000,
        },
      })
    },
  })

  // Function to fetch group info from API
  const fetchGroupInfo = async () => {
    setLoading(true)
    setError(null)

    // Check if SERVER_API_PATH_GROUP is undefined or empty
    if (!SERVER_API_PATH_GROUP) {
      setError("API configuration missing")
      setLoading(false)
      return
    }

    // Check if extractedGroupId is available
    if (!extractedGroupId) {
      setError("Group ID missing")
      setLoading(false)
      return
    }

    try {
      const apiUrl = `${SERVER_API_PATH_GROUP}/group/info?whatsappGroupId=${extractedGroupId}`

      const headers = {
        "Content-Type": "application/json",
        "X-Woztell-Context": contextToken || "",
      }
      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.ok === 1) {
        // Transform API response to component format
        const transformedGroupData = {
          id: data?._id,
          name: data?.subject,
          description: data?.description,
          inviteLink: data?.inviteLink || "",
          totalParticipants: data?.total_participant_count,
          creationTimestamp: data?.creation_timestamp,
          suspended: data?.suspended,
          profilePictureUrl: data?.profilePicture?.next?.url || data?.profilePicture?.url || null,
        }

        const transformedMembers = (data?.participants && Array.isArray(data?.participants))
          ? data?.participants.map((participant) => ({
            id: participant.externalId,
            name: participant.firstName
              ? `${participant.firstName} ${participant.lastName || ""}`.trim()
              : participant.name || participant.externalId,
            isYou: false, // You'll need logic to determine this
            isAdmin: false, // You'll need logic to determine admin status
            status: "Online", // Default status, you might get this from another field
            profilePictureUrl: participant.avatar || null,
            userName: participant.userName || null,
            gender: participant.gender || null,
            locale: participant.locale || null,
          }))
          : []

        setGroupData(transformedGroupData || {})
        setMembers(transformedMembers || [])
      } else {
        throw new Error("API returned error response")
      }
    } catch (apiError) {
      setError(apiError.message)
      setGroupData({})
      setMembers([])
    } finally {
      setLoading(false)
    }
  }

  // Function to fetch join requests from API
  const fetchJoinRequests = async (before = null, after = null) => {
    setLoadingJoinRequests(true)
    setJoinRequestsError(null)

    // Check if SERVER_API_PATH_GROUP is undefined or empty
    if (!SERVER_API_PATH_GROUP) {
      setJoinRequestsError("API configuration missing")
      setLoadingJoinRequests(false)
      return
    }

    // Check if extractedGroupId is available
    if (!extractedGroupId) {
      setJoinRequestsError("Group ID missing")
      setLoadingJoinRequests(false)
      return
    }

    try {
      let apiUrl = `${SERVER_API_PATH_GROUP}/group/join-requests?groupId=${extractedGroupId}`
      if (before) apiUrl += `&before=${before}`
      if (after) apiUrl += `&after=${after}`

      const headers = {
        "Content-Type": "application/json",
        "X-Woztell-Context": contextToken || "",
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.ok === 1) {
        const transformedJoinRequests = (data?.data && Array.isArray(data?.data))
          ? data?.data.map((request) => ({
            id: request.join_request_id,
            waId: request.wa_id,
            name: `User ${request.wa_id}`, // You might need to get name from another source
            creationTimestamp: request.creation_timestamp,
            profilePictureUrl: null,
          }))
          : []

        setJoinRequests(transformedJoinRequests || [])
      } else {
        throw new Error("API returned error response")
      }
    } catch (apiError) {
      setJoinRequestsError(apiError.message)
      setJoinRequests([])
    } finally {
      setLoadingJoinRequests(false)
    }
  }

  // Fetch group info when component mounts
  useEffect(() => {
    if (extractedGroupId) {
      fetchGroupInfo()
      fetchJoinRequests() // Also fetch join requests
    }
  }, [extractedGroupId])

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close member dropdown if clicking outside
      if (dropdownVisible && !event.target.closest("[data-member-dropdown]")) {
        setDropdownVisible(null)
      }
      // Close join request dropdown if clicking outside
      if (joinRequestDropdownVisible && !event.target.closest("[data-join-request-dropdown]")) {
        setJoinRequestDropdownVisible(null)
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [dropdownVisible, joinRequestDropdownVisible])

  // Refresh Invite Link Hook
  const refreshInviteLinkMutation = useMutate({
    url: `${SERVER_API_PATH_GROUP}/group/invite-link`,
    contextToken,
    method: "PATCH",
    errorPaths: ["error", "errorMessage", "error.message"],
    onSuccess: () => {
      setRefreshingLink(false)
      sendToastMessageToInbox({
        message: "Link refreshed successfully",
        type: "success",
        options: {
          duration: 5000,
        },
      })
      fetchGroupInfo() // Refresh the group data to get the new invite link
    },
    onError: () => {
      setRefreshingLink(false)
      sendToastMessageToInbox({
        message: "Something went wrong. Failed to refresh link",
        type: "error",
        options: {
          duration: 5000,
        },
      })
    },
    onFailure: () => {
      setRefreshingLink(false)
      sendToastMessageToInbox({
        message: "Something went wrong. Failed to refresh link",
        type: "error",
        options: {
          duration: 5000,
        },
      })
    },
  })

  // Delete Invite Link Hook
  // const deleteInviteLinkMutation = useMutate({
  //   url: `${SERVER_API_PATH_GROUP}/group/invite-link`,
  //   contextToken,
  //   method: "PATCH",
  //   errorPaths: ["error", "errorMessage", "error.message"],
  //   onSuccess: () => {
  //     setDeletingLink(false)
  //     sendToastMessageToInbox({
  //       message: "Link deleted successfully",
  //       type: "success",
  //       options: {
  //         duration: 5000,
  //       },
  //     })
  //     fetchGroupInfo() // Refresh the group data
  //   },
  //   onError: () => {
  //     setDeletingLink(false)
  //     sendToastMessageToInbox({
  //       message: "Something went wrong. Failed to delete link",
  //       type: "error",
  //       options: {
  //         duration: 5000,
  //       },
  //     })
  //   },
  //   onFailure: () => {
  //     setDeletingLink(false)
  //     sendToastMessageToInbox({
  //       message: "Something went wrong. Failed to delete link",
  //       type: "error",
  //       options: {
  //         duration: 5000,
  //       },
  //     })
  //   },
  // })

  // Approve Join Request Hook
  const approveJoinRequestMutation = useMutate({
    url: `${SERVER_API_PATH_GROUP}/group/approve-join-requests`,
    contextToken,
    method: "POST",
    errorPaths: ["error", "errorMessage", "error.message"],
    onSuccess: (data, variables) => {
      const requestId = variables?.requestId
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[requestId]
        return updated
      })

      sendToastMessageToInbox({
        message: "Join request approved successfully",
        type: "success",
        options: {
          duration: 5000,
        },
      })

      // Remove the approved request from the list
      setJoinRequests((prev) => prev.filter((req) => req.id !== requestId))

      // Refresh group data to update member count
      fetchGroupInfo()
    },
    onError: (err, variables) => {
      const requestId = variables?.requestId
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[requestId]
        return updated
      })

      sendToastMessageToInbox({
        message: "Something went wrong. Failed to approve request",
        type: "error",
        options: {
          duration: 5000,
        },
      })
    },
    onFailure: (err, variables) => {
      const requestId = variables?.requestId
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[requestId]
        return updated
      })

      sendToastMessageToInbox({
        message: "Something went wrong. Failed to approve request",
        type: "error",
        options: {
          duration: 5000,
        },
      })
    },
  })

  // Reject Join Request Hook
  const rejectJoinRequestMutation = useMutate({
    url: `${SERVER_API_PATH_GROUP}/group/reject-join-requests`,
    contextToken,
    method: "POST",
    errorPaths: ["error", "errorMessage", "error.message"],
    onSuccess: (data, variables) => {
      const requestId = variables?.requestId
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[requestId]
        return updated
      })

      sendToastMessageToInbox({
        message: "Join request rejected successfully",
        type: "success",
        options: {
          duration: 5000,
        },
      })

      // Remove the rejected request from the list
      setJoinRequests((prev) => prev.filter((req) => req.id !== requestId))
    },
    onError: (err, variables) => {
      const requestId = variables?.requestId
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[requestId]
        return updated
      })

      sendToastMessageToInbox({
        message: "Something went wrong. Failed to reject request",
        type: "error",
        options: {
          duration: 5000,
        },
      })
    },
    onFailure: (err, variables) => {
      const requestId = variables?.requestId
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[requestId]
        return updated
      })

      sendToastMessageToInbox({
        message: "Something went wrong. Failed to reject request",
        type: "error",
        options: {
          duration: 5000,
        },
      })
    },
  })

  const handleToggleExpand = () => {
    const newExpandedState = !isExpanded
    setIsExpanded(newExpandedState)

    // Send message to parent window to update iframe height
    if (window.parent !== window) {
      window.parent.postMessage({
        type: "SECTION_TOGGLE",
        payload: {
          sectionId: sectionId || "group_members", // Use the section ID from URL or default
          isExpanded: newExpandedState,
          height: newExpandedState ? "400px" : "50px", // Send the height information
        },
      }, "*")
    }
  }

  const handleCopyInviteLink = () => {
    if (groupData?.inviteLink) {
      navigator.clipboard.writeText(groupData?.inviteLink)
      setCopyTooltipVisible(true)
      // Hide tooltip after 2 seconds
      setTimeout(() => {
        setCopyTooltipVisible(false)
      }, 2000)
    }
  }

  const handleInviteLinkClick = () => {
    if (groupData?.inviteLink) {
      navigator.clipboard.writeText(groupData?.inviteLink)
      setTooltipVisible(true)
      // Hide tooltip after 2 seconds
      setTimeout(() => {
        setTooltipVisible(false)
      }, 2000)
    }
  }

  const handleRemoveMember = (memberId) => {
    const removeParticipants = [{ user: memberId }]

    let hasErrors = false
    if (removeParticipants.length === 0) {
      sendToastMessageToInbox({
        message: "No members selected for removal",
        type: "error",
        options: {
          duration: 5000,
        },
      })
      hasErrors = true
    }

    if (!hasErrors) {
      const updateGroupBody = {
        groupId: extractedGroupId,
        channelId: wChannelId,
      }

      if (removeParticipants.length) {
        updateGroupBody.removeParticipants = removeParticipants
      }

      updateGroup.mutate(updateGroupBody)
    }
  }

  const handleAddMember = () => {
    // Send message to parent window with static section id
    if (window.parent !== window) {
      window.parent.postMessage({
        type: "OPEN_INTEGRATION_MODAL",
        payload: {
          sectionId: "add_group_members",
          groupInviteLink: groupData?.inviteLink || "",

        },
      }, "*")
    } else {
      // Fallback
    }
  }

  const handleRefreshLink = () => {
    setRefreshingLink(true)

    setTimeout(() => {
      setRefreshingLink(false)
    }, 30000) // 30 seconds timeout

    refreshInviteLinkMutation.mutate({
      groupId: extractedGroupId,
      action: "REFRESH",
    })
  }

  // const handleDeleteLink = () => {
  //   setDeletingLink(true)

  //   setTimeout(() => {
  //     setDeletingLink(false)
  //   }, 30000) // 30 seconds timeout

  //   deleteInviteLinkMutation.mutate({
  //     groupId: extractedGroupId,
  //     action: "DELETE",
  //   })
  // }

  const toggleDropdown = (memberId) => {
    setDropdownVisible((prev) => (prev === memberId ? null : memberId))
  }

  const handleMouseEnter = (e, color) => {
    e.target.style.backgroundColor = color
  }

  const handleMouseLeave = (e, color) => {
    e.target.style.backgroundColor = color
  }

  const handleRemoveMemberClick = (member) => {
    setSelectedMember(member)
    setPopupVisible(true)
  }

  const handleMemberInfoClick = (member) => {
    // Send message to parent window to open MemberEditModal
    const message = {
      type: "OPEN_MEMBER_EDIT_MODAL",
      payload: {
        member,
        sectionId,
      },
    }

    window.parent.postMessage(message, "*")
    setDropdownVisible(null)
  }

  const closePopup = () => {
    setPopupVisible(false)
    setSelectedMember(null)
  }

  const confirmRemoveMember = () => {
    if (selectedMember) {
      handleRemoveMember(selectedMember.id)
      // Close popup immediately, member state will be cleared on success
      setPopupVisible(false)
    }
  }

  const handleDeleteGroup = () => {
    if (window.confirm(`Are you sure you want to delete "${groupData?.name || "this group"}"? This action cannot be undone.`)) {
      setDeletingGroup(true)

      setTimeout(() => {
        setDeletingGroup(false)
      }, 30000) // 30 seconds timeout

      deleteGroup.mutate({
        groupId: extractedGroupId,
      })
    }
  }

  const handleApproveRequest = (request) => {
    setApprovingRequests((prev) => ({
      ...prev,
      [request.id]: true,
    }))

    // Set timeout to reset approving state in case of no response
    setTimeout(() => {
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[request.id]
        return updated
      })
    }, 30000) // 30 seconds timeout

    approveJoinRequestMutation.mutate({
      groupId: extractedGroupId,
      // eslint-disable-next-line camelcase
      join_requests: [request.id],
    })
  }

  const handleRejectRequest = (request) => {
    setApprovingRequests((prev) => ({
      ...prev,
      [request.id]: true,
    }))

    // Set timeout to reset processing state in case of no response
    setTimeout(() => {
      setApprovingRequests((prev) => {
        const updated = { ...prev }
        delete updated[request.id]
        return updated
      })
    }, 30000) // 30 seconds timeout

    rejectJoinRequestMutation.mutate({
      groupId: extractedGroupId,
      // eslint-disable-next-line camelcase
      join_requests: [request.id],
    })
  }

  const handleTabSwitch = (tab) => {
    setActiveTab(tab)
    if (tab === "requests" && joinRequests.length === 0 && !loadingJoinRequests) {
      fetchJoinRequests()
    }
  }

  return (
    <FormContainer>
      {/* Group Members Header */}
      <MembersHeader
        isExpanded={isExpanded}
        $disabled={!(!loading && !error && groupId && groupData?.inviteLink)}
      >
        <div
          className="left-section"
          onClick={handleToggleExpand}
        >
          <FontAwesomeIcon
            icon={faUserPlus}
            size="sm"
            className="group-icon"
          />
          <div className="title">
            Group Members
          </div>
        </div>
        <div className="right-section">
          <div
            className="add-member"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (!loading && !error && groupId && groupData?.inviteLink) {
                handleAddMember()
              }
            }}
          >
            <FontAwesomeIcon
              icon={faPlus}
              size="sm"
            />
            <span>Member</span>
          </div>
          <FontAwesomeIcon
            className="chevron-icon"
            icon={faChevronDown}
            size="sm"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleToggleExpand()
            }}
          />
        </div>
      </MembersHeader>

      {/* Group Members Content */}
      <MembersContent isExpanded={isExpanded}>
        {/* Invite Link Section */}

        <InviteSection>
          <InviteLink>
            <InviteLinkText
              onClick={handleInviteLinkClick}
            >
              {groupData?.inviteLink || "Link not available"}
              <LinkTooltip visible={tooltipVisible}>
                Link copied!
              </LinkTooltip>
            </InviteLinkText>
            <IconButton className="copy" onClick={handleCopyInviteLink} title="Copy link">
              <FontAwesomeIcon icon={faLink} />
              <Tooltip visible={copyTooltipVisible}>
                Link copied!
              </Tooltip>
            </IconButton>
            <IconButton
              className="refresh"
              onClick={handleRefreshLink}
              title={refreshingLink ? "Refreshing..." : "Refresh link"}
              disabled={refreshingLink}
            >
              {refreshingLink ? (
                <LoadingSpinner />
              ) : (
                <FontAwesomeIcon icon={faRefresh} />
              )}
            </IconButton>
            {/* <IconButton
              className="delete"
              onClick={handleDeleteLink}
              title={deletingLink ? "Deleting..." : "Delete link"}
              disabled={refreshingLink || deletingLink}
            >
              {deletingLink ? (
                <LoadingSpinner />
              ) : (
                <FontAwesomeIcon icon={faX} />
              )}
            </IconButton> */}
          </InviteLink>
        </InviteSection>

        {/* Tabs for Members and Join Requests */}
        <TabContainer>
          <Tab
            active={activeTab === "members"}
            isMembersTab
            onClick={() => handleTabSwitch("members")}
          >
            Members
            <span className="count">
              {members.length}
            </span>
          </Tab>
          <Tab
            active={activeTab === "requests"}
            isJoinRequestsTab
            onClick={() => handleTabSwitch("requests")}
          >
            Join Requests
            <span className="count">
              {joinRequests.length}
            </span>
          </Tab>
        </TabContainer>

        {/* Members List */}
        {activeTab === "members" && (
          <MembersSection hasMembers={loading || members.length > 0}>
            <MembersList hasMembers={loading || members.length > 0}>
              {loading && <MemberListSkeleton count={6} />}
              {
                error && (
                  <MemberRow>
                    <p style={{ color: "#ef4444", fontSize: "14px" }}>
                      Something went wrong
                      <span
                        style={{
                          textDecoration: "underline", cursor: "pointer", marginLeft: "6px", color: "#007AFF",
                        }}
                        onClick={fetchGroupInfo}
                      >
                        {" "}
                        Retry
                      </span>
                    </p>
                  </MemberRow>
                )
              }
              {!loading && !error && members?.length === 0 && (
                <MemberRow style={{ background: "#F0F2F5" }}>
                  <span style={{ color: theme.gray5, fontSize: "13px" }}>No members yet</span>
                </MemberRow>
              )}
              {!loading && members?.length > 0 && members?.map((member, index) => (
                <MemberRow key={member.id || index}>
                  <MemberInfo>
                    <FontAwesomeIcon
                      icon={faUserCircle}
                      size="sm"
                      className="user-icon"
                      style={{ color: "#A5A5A7", fontSize: "30px" }}
                    />
                    <MemberDetails>
                      <MemberName>
                        {member.isYou ? "You" : member.name || member.phone}
                      </MemberName>
                      {/* <MemberSubtitle>
                        {member.status && `${member.status}`}
                      </MemberSubtitle> */}
                    </MemberDetails>
                  </MemberInfo>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {member.id && (
                      <MemberStatus>Member</MemberStatus>
                    )}
                    {!member.isYou && (
                      <div style={{ position: "relative" }} data-member-dropdown>
                        <ActionButton
                          className="info"
                          onClick={() => toggleDropdown(member.id)}
                          title="Options"
                        >
                          <FontAwesomeIcon icon={faChevronDown} />
                        </ActionButton>
                        {dropdownVisible === member.id && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-70%",
                              right: "100%",
                              background: "white",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              borderRadius: "4px",
                              zIndex: 10,
                              width: "150px",
                              fontSize: "12px",
                            }}
                          >
                            <div
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                transition: "background-color 0.2s ease",
                              }}
                              onMouseEnter={(e) => handleMouseEnter(e, "#f0f0f0")}
                              onMouseLeave={(e) => handleMouseLeave(e, "white")}
                              onClick={() => handleMemberInfoClick(member)}
                            >
                              Member Info
                            </div>
                            <div
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                color: "#ef4444",
                                transition: "background-color 0.2s ease",
                              }}
                              onMouseEnter={(e) => handleMouseEnter(e, "#f0f0f0")}
                              onMouseLeave={(e) => handleMouseLeave(e, "white")}
                              onClick={() => handleRemoveMemberClick(member)}
                            >
                              Remove Member
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </MemberRow>
              ))}
            </MembersList>
          </MembersSection>
        )}

        {/* Join Requests List */}
        {activeTab === "requests" && (
          <MembersSection hasMembers={loadingJoinRequests || joinRequests.length > 0}>
            <MembersList hasMembers={loadingJoinRequests || joinRequests.length > 0}>
              {loadingJoinRequests && <MemberListSkeleton count={3} />}
              {
                joinRequestsError && (
                  <MemberRow>
                    <p style={{ color: "#ef4444", fontSize: "14px" }}>
                      Something went wrong
                      <span
                        style={{
                          textDecoration: "underline", cursor: "pointer", marginLeft: "6px", color: "#007AFF",
                        }}
                        onClick={() => fetchJoinRequests()}
                      >
                        {" "}
                        Retry
                      </span>
                    </p>
                  </MemberRow>
                )
              }
              {!loadingJoinRequests && !joinRequestsError && joinRequests?.length === 0 && (
                <MemberRow style={{ background: "#F0F2F5" }}>
                  <span style={{ color: theme.gray5, fontSize: "13px" }}>No join requests found</span>
                </MemberRow>
              )}
              {!loadingJoinRequests && joinRequests?.length > 0 && joinRequests?.map((request, index) => (
                <MemberRow key={request.id || index}>
                  <MemberInfo>
                    <FontAwesomeIcon
                      icon={faUserCircle}
                      size="sm"
                      className="user-icon"
                      style={{ color: "#A5A5A7", fontSize: "30px" }}
                    />
                    <MemberDetails>
                      <MemberName>
                        {request.waId || "N/A"}
                      </MemberName>
                      <MemberSubtitle>
                        {new Date(request.creationTimestamp).toLocaleDateString()}
                      </MemberSubtitle>
                    </MemberDetails>
                  </MemberInfo>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {/* <MemberStatus>Pending</MemberStatus> */}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <RequestActionButton
                        className="approve"
                        onClick={() => handleApproveRequest(request)}
                        disabled={approvingRequests[request.id]}
                        onMouseEnter={() => setActionTooltips((prev) => ({ ...prev, [`approve-${request.id}`]: true }))}
                        onMouseLeave={() => setActionTooltips((prev) => ({ ...prev, [`approve-${request.id}`]: false }))}
                      >
                        {approvingRequests[request.id] ? (
                          <LoadingSpinner />
                        ) : (
                          <FontAwesomeIcon icon={faCheck} />
                        )}
                        <ActionTooltip visible={actionTooltips[`approve-${request.id}`]}>
                          Accept
                        </ActionTooltip>
                      </RequestActionButton>
                      <RequestActionButton
                        className="reject"
                        onClick={() => handleRejectRequest(request)}
                        disabled={approvingRequests[request.id]}
                        onMouseEnter={() => setActionTooltips((prev) => ({ ...prev, [`reject-${request.id}`]: true }))}
                        onMouseLeave={() => setActionTooltips((prev) => ({ ...prev, [`reject-${request.id}`]: false }))}
                      >
                        {approvingRequests[request.id] ? (
                          <LoadingSpinner />
                        ) : (
                          <FontAwesomeIcon icon={faTimes} />
                        )}
                        <ActionTooltip visible={actionTooltips[`reject-${request.id}`]}>
                          Reject
                        </ActionTooltip>
                      </RequestActionButton>
                    </div>
                  </div>
                </MemberRow>
              ))}
            </MembersList>
          </MembersSection>
        )}
      </MembersContent>

      {/* Confirmation Popup */}
      {popupVisible && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "white",
            border: "1px solid red",
            borderRadius: "8px",
            padding: "20px",
            zIndex: 1000,
            fontSize: "14px",
            width: "310px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          <p style={{ margin: "0 0 16px 0", textAlign: "center", color: "#333" }}>
            Remove
            {" "}
            {selectedMember?.name}
            {" "}
            from
            {" "}
            {groupData?.name || "this group"}
            ?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
            <button
              type="button"
              style={{
                background: "none",
                color: "#007AFF",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                padding: "8px 16px",
                border: "1px solid #D7D7D7",
                borderRadius: "4px",
              }}
              onClick={closePopup}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                background: "#FF3B30",
                border: "none",
                color: "white",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
              }}
              onClick={confirmRemoveMember}
            >
              Delete
            </button>
          </div>
        </div>
      )}
      <DeleteGroupButton
        onClick={handleDeleteGroup}
        className={deletingGroup ? "disabled" : ""}
        style={{
          pointerEvents: deletingGroup ? "none" : "auto",
        }}
      >
        {deletingGroup ? (
          <LoadingSpinner />
        ) : (
          <FontAwesomeIcon
            className="delete-icon"
            icon={faTrash}
          />
        )}
        <span>{deletingGroup ? "Deleting group..." : "Delete group"}</span>
      </DeleteGroupButton>
    </FormContainer>
  )
}

export default GroupMembers
